// bluaIQ — NBA-1 follow-up: bilingual display localization for AI-generated
// bid risk / scope-gap analysis notes.
//
// FE-only, pure (no React) so it can be unit-tested directly. The stored AI
// text is NEVER mutated — this only produces a Spanish DISPLAY string for the
// known risk-note themes the bid reader (BID_PROMPT) emits in consistent
// phrasing.
//
// When a note matches a known theme → returns a Spanish user-facing sentence.
// When a note cannot be safely localized → returns null, and the caller shows
// the ORIGINAL extracted text under a clearly-labeled "Texto original
// extraído" / "Original extracted text" wrapper so source evidence stays
// honest and visually distinct from bluaIQ's own interpretation.
//
// IMPORTANT: this is interpretation for DISPLAY only. It does not claim the
// contractor wrote in Spanish, and it never rewrites or persists the raw note.

// Bilingual label for the "original extracted text" wrapper used when a note
// cannot be localized. EN/ES.
export const ORIGINAL_EXTRACTED_TEXT_LABEL = {
  en: 'Original extracted text',
  es: 'Texto original extraído',
} as const

/**
 * Localize a known AI-generated risk / scope-gap note into a Spanish
 * user-facing sentence. Returns null when no known theme matches (the caller
 * then shows the raw text under the "original extracted text" label).
 *
 * Themes (matched on prompt-consistent phrasing, most specific first):
 *   1. Insurance not mentioned (GL / workers' comp / liability)
 *   2. Ambiguous / combined / mislabeled pricing
 *   3. Fixtures not included / labor-and-materials-only
 *   4. High deposit / large upfront payment
 *   5. Short bid validity / expiration window
 *   6. Market / amount-range verification
 *   7. Incomplete scope / many exclusions
 */
export function localizeRiskNote(note: string): string | null {
  const f = (note ?? '').toLowerCase()
  if (!f.trim()) return null

  // 1) Insurance not mentioned (GL / workers' comp / liability)
  if (/insurance|workers?[\s'’-]*comp|liability|\bgl\b|\bw\/?c\b/.test(f)) {
    return 'No se menciona seguro de responsabilidad general (GL) ni compensación laboral (workers’ compensation) en el documento; verifica la cobertura antes de contratar.'
  }
  // 2) Ambiguous / combined / mislabeled pricing
  if (
    /(ambiguous|unclear|combined|misread|mislabel|labeled|label\b)/.test(f) &&
    /(pric|option|electric|gas|cost|total|amount)/.test(f)
  ) {
    return 'Los precios indicados son ambiguos; se requiere aclaración antes de comparar la propuesta.'
  }
  // 3) Fixtures not included / labor-and-materials-only
  if (/fixture/.test(f)) {
    return 'La propuesta no incluye fixtures/accesorios; se requiere confirmación del alcance de materiales.'
  }
  // 4) High deposit / large upfront payment
  if (
    /(deposit|upfront|down[\s-]?payment|advance)/.test(f) &&
    /(high|large|excess|steep|[3-9]\d\s?%|40|50|60)/.test(f)
  ) {
    return 'El depósito o anticipo solicitado es elevado; revisa las condiciones de pago antes de aceptar.'
  }
  // 5) Short bid validity / expiration window
  if (/(valid|validity|expir)/.test(f)) {
    return 'La propuesta tiene una vigencia corta; confirma la fecha de expiración antes de decidir.'
  }
  // 6) Market / amount-range verification (price range or "for a … rough-in")
  if (
    /(below[\s-]?market|above[\s-]?market|market[\s-]?(rate|price|value)|comparable\s?price)/.test(f) ||
    /\$\s?[\d,]+(\.\d+)?\s?[–—-]\s?\$?\s?[\d,]+/.test(f) ||
    (/\bfor a\b/.test(f) && /(construction|rough[\s-]?in|plumbing|electrical|hvac|framing|roofing)/.test(f))
  ) {
    return 'El monto debe verificarse contra el alcance del trabajo y los precios de mercado de referencia.'
  }
  // 7) Incomplete scope / many exclusions
  if (
    /(incomplete\s?scope|scope\s?(is\s?)?incomplete|many\s?exclusion|multiple\s?exclusion|missing\s?scope|several\s?exclusion)/.test(f)
  ) {
    return 'El alcance parece incompleto o tiene varias exclusiones; solicita un desglose detallado.'
  }

  // No safe localization — caller shows the original note under a clear label.
  return null
}
